import React, { useEffect, FunctionComponent, useState, MouseEvent } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IAppState } from '../../../../common/models/store/IAppState';
import { fetchCandidatesByContactRoutine } from '../../routines';
import Table from '../../../../components/Table';
import { getColumns } from './utils';
import { IFilter } from '../../../../common/models/filter/IFilter';
import { equals, pipe, map, filter, isNil, apply, sum } from 'ramda';
import { IContactFilter } from '../../../../common/models/filter/IContactFilter';
import { ICandidateScore } from '../../../../common/models/candidate/ICandidateScore';
import { ContactType } from '../../../../common/enums/ContactType';
import { ISelectedRole } from '../../../../common/models/contact/ISelectedRole';
import { IRowAccessor } from '../../../../common/models/table/IRowAccessor';
import {
  showSurveyBuilderModalRoutine,
  showContactProfileModalRoutine
} from '../../../../routines/history';
import AvgScore from '../../../../components/AvgScore';
import { IContact } from '../../../../common/models/contact/IContact';
import RegularButton from '../../../../components/RegularButton';
import Icon from '../../../../components/Icon';
import styles from './styles.module.scss';

interface IReferencePaneProps {
  contactId: string;
  contact: IContact;
  candidates: ICandidateScore[];
  loading: boolean;
  fetchCandidates: IBindingCallback1<IContactFilter>;
  showContactProfileModal: IBindingCallback1<ISelectedRole | undefined>;
  showSurveyBuilderModal: IBindingCallback1<string>;
}

const ReferencePane: FunctionComponent<IReferencePaneProps> = ({
  contactId,
  candidates,
  loading,
  contact,
  fetchCandidates,
  showContactProfileModal,
  showSurveyBuilderModal
}) => {
  const defaultFilter: IContactFilter = {
    id: contactId,
    sort: [{ id: 'date', desc: true }],
    filter: []
  };
  const [tableFilter, setFilter] = useState(defaultFilter);

  useEffect(() => {
    fetchCandidates(tableFilter);
  }, [tableFilter]);

  const updateFilter = (newFilter: Partial<IFilter>) => {
    const updatedFilter = { ...tableFilter, ...newFilter };

    if (!equals(updatedFilter, tableFilter)) {
      setFilter(updatedFilter);
    }
  };

  const onCellClick = ({ accessor, original }: IRowAccessor<ICandidateScore>, event: MouseEvent<HTMLElement>) => {
    switch (accessor) {
      case 'name':
        showContactProfileModal({
          contactId: original.contactId,
          candidateId: original.id,
          role: ContactType.Candidate
        });
        break;
      case 'survey':
        event.stopPropagation();
        break;
      default:
        break;
    }
  };

  const scores = pipe<ICandidateScore[], number[], number[]>(
    map(({ score }) => score),
    filter(score => !isNil(score))
  )(candidates);

  const highestScore = scores.length ? apply(Math.max, scores) : 0;
  const lowestScore = scores.length ? apply(Math.min, scores) : 0;
  const averageScore = scores.length ? sum(scores) / scores.length : 0;

  return (
    <>
      <div className="profile-pane-main">
        <div className={styles.referenceProfileHeader}>
          <FormattedMessage
            id="contactProfile.reference.title"
            defaultMessage="{name} has been a reference for these candidates"
            values={{ name: contact.firstName }}
          />
          {contact.email && (
            <RegularButton
              icon={<Icon icon="email" />}
              textIntlId="emailReference"
              defaultMessage="Email reference"
              variant="secondary"
              onClick={() => window.open(`mailto:${contact.email}`)}
            />
          )}
        </div>
        <Table
          config={{
            columns: getColumns(showSurveyBuilderModal),
            onFilterUpdated: updateFilter,
            data: { items: candidates, total: candidates.length },
            loading,
            sortingEnabled: true,
            filteringEnabled: true,
            onCellClicked: onCellClick,
            defaultSorted: tableFilter.sort
          }}
        />
      </div>
      <footer className="profile-pane-footer d-flex justify-content-end">
        <AvgScore
          score={highestScore}
          intlId="contactProfile.reference.highest"
          defaultMessage="Highest score given"
        />
        <AvgScore
          score={lowestScore}
          intlId="contactProfile.reference.lowest"
          defaultMessage="Lowest score given"
        />
        <AvgScore
          score={averageScore}
          intlId="contactProfile.reference.average"
          defaultMessage="Average score given"
        />
      </footer>
    </>
  );
};

const mapStateToProps = ({
  contactProfile: {
    candidates: {
      items,
      loading
    },
    contact: { contact, contactId }
  }
}: IAppState) => ({ candidates: items, loading, contact, contactId });

const mapDispachToProps = {
  fetchCandidates: fetchCandidatesByContactRoutine,
  showSurveyBuilderModal: showSurveyBuilderModalRoutine,
  showContactProfileModal: showContactProfileModalRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(ReferencePane);
